"use client";
import React, { useMemo } from "react";
import Image from "next/image";
import Link from "next/link";
import { Menu } from "lucide-react";

import TopBanner from "../layout/TopBanner";
import Drawer from "../layout/navbar/mobile/Drawer";
import useNavbar from "../layout/navbar/useNavbar";
import { useFormationsDrawer } from "../layout/navbar/useFormationsDrawer";
import {
  NAVBAR_ACTIONS,
  NAVBAR_LINKS,
  SUB_DRAWERS,
} from "../layout/navbar/navbarData";
import type { SubDrawer } from "../layout/navbar/navbarData";
import FicheAnchorNav, { useFicheSections } from "./FicheAnchorNav";
import useMediaQuery from "@/hooks/useMediaQuery";
import useScrollReveal from "@/hooks/useScrollReveal";

/* Under this offset the hero — with its own navbar and pill row — is still on
   screen, so pinning a second header would only duplicate it. */
const REVEAL_AFTER_PX = 300;

/* Height of the revealed bar: banner (35) + logo row (58) + pill row (51).
   The anchored sections carry it as `scroll-mt-36` (144px) so a pill never
   parks its section underneath the header. Keep the two in step. */

/**
 * Mobile-only compact header: slides in as soon as the reader scrolls back up,
 * slides away again on the way down (where the sticky CTA bar takes over).
 * Carries the site menu and the section anchors, both of which are otherwise
 * stranded at the top of the page.
 */
interface FicheStickyNavProps {
  /** Slug of the fiche being read — settles which anchor pills the row carries. */
  slug: string;
}

const FicheStickyNav: React.FC<FicheStickyNavProps> = ({ slug }) => {
  const isMobile = useMediaQuery("(max-width: 1023px)");
  const sections = useFicheSections(slug);
  const hasScrolledUp = useScrollReveal(REVEAL_AFTER_PX);
  const {
    open,
    activeSubDrawer,
    toggleDrawer,
    closeDrawer,
    openSubDrawer,
    closeSubDrawer,
  } = useNavbar();
  const { drawer: liveFormationsDrawer, subDrawers: liveCatSubDrawers } =
    useFormationsDrawer();

  /* Same merge as the main navbar — live categories once they land, the static
     map meanwhile so the menu is never empty. The categories request is shared
     with the navbar's (SWR dedupes on the key), not doubled. */
  const subDrawers = useMemo<Record<string, SubDrawer>>(() => {
    if (!liveFormationsDrawer) return SUB_DRAWERS;
    return {
      ...SUB_DRAWERS,
      formations: liveFormationsDrawer,
      ...liveCatSubDrawers,
    };
  }, [liveFormationsDrawer, liveCatSubDrawers]);

  /* Desktop keeps the hero navbar and the in-flow pills in view — nothing to pin. */
  if (!isMobile) return null;

  /* An open drawer keeps the bar down, so closing it doesn't strand the burger. */
  const isRevealed = open || hasScrolledUp;

  return (
    <>
      <div
        aria-hidden={!isRevealed}
        className={`fixed inset-x-0 top-0 z-40 bg-white shadow-[0_2px_8px_2px_#0000001A] transition-transform duration-300 ease-in-out ${
          isRevealed ? "translate-y-0" : "-translate-y-full pointer-events-none"
        }`}
      >
        {/* The site's own banner, unchanged — it comes along with the header
            rather than being restyled into it. */}
        <TopBanner />

        <div className="flex items-center justify-between gap-3 px-4 pt-3 pb-2 ">
          <Image
            src="/assets/Gradient-Bckg.svg"
            alt="Hero Background"
            title="Hero Background"
            fill
            priority
            className="object-cover -z-9999"
            placeholder="blur"
            blurDataURL="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJ..."
          />
          <Link href="/" aria-label="Spot Formations — accueil" className="shrink-0">
            <Image
              src="/assets/logo.svg"
              alt="Spot Formations"
              title="Spot Formations"
              width={258}
              height={18}
              className="w-38 h-auto"
            />
          </Link>

          <button
            type="button"
            onClick={toggleDrawer}
            aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
            aria-expanded={open}
            className="-mr-1.5 inline-flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-lg"
          >
            <Menu className="size-7 text-white" aria-hidden="true" />
          </button>
        </div>

        <FicheAnchorNav
          sections={sections}
          className="gap-1.5 px-4 pt-2 pb-2.5 border-t border-[#EFEFEF] bg-white"
        />
      </div>
      <Drawer
        open={open}
        links={NAVBAR_LINKS}
        actions={NAVBAR_ACTIONS}
        subDrawers={subDrawers}
        activeSubDrawer={activeSubDrawer}
        onClose={closeDrawer}
        onOpenSubDrawer={openSubDrawer}
        onCloseSubDrawer={closeSubDrawer}
      />
    </>
  );
};

export default FicheStickyNav;
